import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Avatar,
  CircularProgress,
  IconButton
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationsContext';
import {
  People,
  LocalHospital,
  TrendingUp,
  Warning,
  CalendarToday,
  AttachMoney,
  Speed,
  CloudDone,
  PersonAdd,
  MonitorHeart,
  BusinessCenter,
  Groups,
  AccessTime,
  CheckCircle
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend
} from 'recharts';
import axios from 'axios';
import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent } from '@mui/material';

import { SystemStats } from '../../types';

// Utility function to fix S3 URL format
const fixS3Url = (url: string | undefined): string | undefined => {
  if (!url) return undefined;

  // Check if it's an S3 URL with regional endpoint
  const s3RegionalPattern = /https:\/\/([^.]+)\.s3\.([^.]+)\.amazonaws\.com\/(.+)/;
  const match = url.match(s3RegionalPattern);

  if (match) {
    const bucketName = match[1];
    const path = match[3];

    // Convert to path-style URL (works better with CORS)
    const fixedUrl = `https://${bucketName}.s3.amazonaws.com/${path}`;
    console.log('🔧 Fixed S3 URL:', url, '->', fixedUrl);
    return fixedUrl;
  }

  // If already in standard format, return as is
  return url;
};

// Utility function to get cache-busted image URL
const getCacheBustedImageUrl = (url: string | undefined, timestamp?: number): string | undefined => {
  if (!url) return undefined;

  const fixedUrl = fixS3Url(url);
  if (!fixedUrl) return undefined;

  const ts = timestamp || Date.now();
  return `${fixedUrl}?t=${ts}`;
};

// Utility function to clean up names that have 'None' in them (from backend concatenation)
const cleanName = (name: string | undefined): string | undefined => {
  if (!name) return undefined;

  // Remove 'None' and extra spaces
  return name
    .replace(/\bNone\b/g, '') // Remove the word 'None'
    .replace(/\s+/g, ' ')     // Replace multiple spaces with single space
    .trim();                   // Remove leading/trailing spaces
};

export default function SuperAdminDashboard() {
  const { user } = useAuth();
  const { recentActivities, dashboardStats, todayAppointments, setTodayAppointments, fetchDashboardSummary } = useNotifications();
  const navigate = useNavigate();
  const [updatingAppointment, setUpdatingAppointment] = useState<string | null>(null);
  const [stats, setStats] = useState<SystemStats>({
    totalPatients: 0,
    totalDoctors: 0,
    activeSubscriptions: 0,
    criticalAlerts: 0,
    todayAppointments: 0,
    monthlyRevenue: 0,
    systemUptime: 0,
    averageResponseTime: 0
  });

  const [totalMarketExecutives, setTotalMarketExecutives] = useState(0);
  const [pendingVerificationDoctors, setPendingVerificationDoctors] = useState(0);
  const [doctorsWithZeroPatients, setDoctorsWithZeroPatients] = useState(0);
  const [malePatients, setMalePatients] = useState(0);
  const [femalePatients, setFemalePatients] = useState(0);
  const [activeSubscribers, setActiveSubscribers] = useState(0);
  const [newSubscribersThisWeek, setNewSubscribersThisWeek] = useState(0);
  const [topExecutives, setTopExecutives] = useState<any[]>([]);

  const isFetchingRef = useRef(false);

  // Loading state for initial data fetch
  const [isLoading, setIsLoading] = useState(true);

  // Analytics state
  const [weeklyTrendsData, setWeeklyTrendsData] = useState<any[]>([]);
  const [trendsFilter, setTrendsFilter] = useState<string>('heartrate');
  const [trendsLoading, setTrendsLoading] = useState(false);
  const [trendsError, setTrendsError] = useState<string | null>(null);

  const monthlyData = [
    { name: 'Jan', patients: 120, appointments: 250 },
    { name: 'Feb', patients: 150, appointments: 320 },
    { name: 'Mar', patients: 180, appointments: 380 },
    { name: 'Apr', patients: 200, appointments: 450 },
    { name: 'May', patients: 250, appointments: 520 },
    { name: 'Jun', patients: 280, appointments: 600 }
  ];





  // Fetch Trends Data (Localized loading)
  const fetchTrendsData = useCallback(async (isInitial = false) => {
    if (!user) return;

    try {
      if (!isInitial) setTrendsLoading(true);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const trendsRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/admin-weekly-health-trends?days=7&activity_status=${trendsFilter}`,
        config
      );

      const trendsRaw = trendsRes.data;
      const trendsList = Array.isArray(trendsRaw) ? trendsRaw : (trendsRaw.trends || []);
      setWeeklyTrendsData(trendsList);
    } catch (err) {
      console.error('Error fetching trends data:', err);
      setTrendsError('Failed to load trends data');
    } finally {
      setTrendsLoading(false);
    }
  }, [user, trendsFilter]);

  // Initial mount-only consolidated fetch
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;
      if (isFetchingRef.current) return;

      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        const token = localStorage.getItem('token');
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        console.log('🏁 Starting Initial SuperAdmin Dashboard Load...');

        // 1. We rely on NotificationsProvider for initial summary fetch
        // No need to manually trigger it here on every mount unless explicitly missing
        if (!dashboardStats) {
          console.log('📡 Summary missing, triggering manual fetch...');
          fetchDashboardSummary();
        }

        // 2. Fetch Performance in parallel. 
        // NOTE: Trends is now exclusively handled by the localized useEffect below
        // which triggers as soon as isLoading becomes false.
        const [perfRes] = await Promise.allSettled([
          axios.get(`${process.env.REACT_APP_API_URL}/marketing/executive-performance`, config)
        ]);

        // Process Executive Performance
        if (perfRes.status === 'fulfilled') {
          const resValue = perfRes.value as any;
          const mappedData = Array.isArray(resValue?.data) ? resValue.data.map((item: any, index: number) => ({
            full_name: item.executive_name || item.full_name || 'Unknown Executive',
            active_subscribers: item.total_joins || item.active_subscribers || 0,
            id: item.executive_id || item.id || `exec-${index}-${Date.now()}`
          })) : [];
          setTopExecutives(mappedData.sort((a: any, b: any) => b.active_subscribers - a.active_subscribers).slice(0, 3));
        }

      } catch (err) {
        console.error('Error during initial dashboard load:', err);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
        console.log('✅ Initial load complete');
      }
    };

    fetchInitialData();
  }, [user, fetchDashboardSummary]); // Removed trendsFilter from here

  // Localized filter updates
  useEffect(() => {
    // Only trigger if initial load is finished AND user actually changed a filter
    if (user && !isLoading) {
      console.log('🔄 Triggering localized Trends update...');
      fetchTrendsData();
    }
  }, [trendsFilter, user, isLoading, fetchTrendsData]);

  // Sync dashboard state when context stats change
  useEffect(() => {
    if (dashboardStats) {
      console.log('✅ Dashboard synced with shared stats');
      setStats({
        totalPatients: Number(dashboardStats.total_patients || 0),
        totalDoctors: Number(dashboardStats.active_doctors || 0),
        activeSubscriptions: Number(dashboardStats.active_subscribers || 0),
        criticalAlerts: Number(dashboardStats.total_critical || 0),
        todayAppointments: Number(dashboardStats.today_appointments || 0),
        monthlyRevenue: 0,
        systemUptime: 99.9,
        averageResponseTime: 250
      });

      setActiveSubscribers(Number(dashboardStats.active_subscribers || 0));
      setNewSubscribersThisWeek(Number(dashboardStats.new_subscribers_this_week || 0));
      setTotalMarketExecutives(Number(dashboardStats.marketing_executives || 0));
      setPendingVerificationDoctors(Number(dashboardStats.pending_verification || 0));
      setDoctorsWithZeroPatients(Number(dashboardStats.doctors_with_no_patients || 0));
      setMalePatients(Number(dashboardStats.male_patients || 0));
      setFemalePatients(Number(dashboardStats.female_patients || 0));
    }
  }, [dashboardStats]);

  const handleMarkAsCompleted = async (appointmentId: string) => {
    try {
      setUpdatingAppointment(appointmentId);
      const token = localStorage.getItem('token');
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      // Update appointment status to completed
      await axios.put(
        `${process.env.REACT_APP_API_URL}/appointments/${appointmentId}`,
        { status: 'completed' },
        config
      );

      // Update global context state
      setTodayAppointments((prevAppointments: any[]) =>
        prevAppointments.map((apt: any) =>
          apt.id === appointmentId ? { ...apt, status: 'completed' } : apt
        )
      );

      console.log('✅ Appointment marked as completed successfully');
    } catch (error) {
      console.error('❌ Error marking appointment as completed:', error);
      alert('Failed to mark appointment as completed. Please try again.');
    } finally {
      setUpdatingAppointment(null);
    }
  };


  // Handle activity click to navigate to the relevant page
  const handleActivityClick = (activity: any) => {
    const navigateTo = activity.navigateTo || '';
    const highlightId = activity.entityId;
    const search = activity.search || '';

    if (navigateTo) {
      console.log(`🔗 Dashboard Activity: Navigating to ${navigateTo} for ${activity.type} with highlightId: ${highlightId}, search: ${search}`);
      navigate(navigateTo, {
        state: { 
          highlightId,
          search
        }
      });
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle, titleVariant = 'h6', fontWeight = 'bold', onClick }: any) => (
    <Card
      elevation={onClick ? 2 : 1}
      onClick={onClick}
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick ? {
          transform: 'translateY(-4px)',
          boxShadow: 4,
          '& .icon-box': {
            transform: 'scale(1.1)',
            opacity: 1
          }
        } : {}
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography color="textSecondary" gutterBottom variant={titleVariant} sx={{ fontWeight }}>
              {title}
            </Typography>
            <Typography variant="h4" component="h2" sx={{ color }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="textSecondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            className="icon-box"
            sx={{
              color,
              opacity: 0.8,
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="textSecondary">
          Loading Dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Super Admin Dashboard
      </Typography>

      {/* Admin Profile */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Avatar
              src={getCacheBustedImageUrl(user?.profile_image)}
              imgProps={{
                crossOrigin: "anonymous",
                onError: (e: any) => {
                  console.error('❌ Admin avatar image failed to load:', user?.profile_image);
                  console.error('Error event:', e);
                  console.error('This is likely a CORS or S3 permissions issue');
                  // Try to fetch the image to get more details
                  fetch(user?.profile_image || '', { mode: 'no-cors' })
                    .then(() => console.log('✅ Image exists but CORS is blocking it'))
                    .catch(err => console.error('❌ Image fetch error:', err));
                }
              }}
              sx={{
                width: 80,
                height: 80,
                bgcolor: user?.profile_image ? 'transparent' : 'primary.main',
                fontSize: '2rem',
                border: user?.profile_image ? '2px solid #e0e0e0' : '2px solid',
                borderColor: user?.profile_image ? 'primary.light' : 'primary.light'
              }}
            >
              {(!user?.profile_image && (user?.full_name || user?.username)?.charAt(0)?.toUpperCase()) || 'S'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom>
                Welcome, {user?.full_name || user?.username}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Super Administrator • System Overview
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Alerts */}
      {stats.criticalAlerts > 0 && (
        <Alert severity="error" sx={{ mb: 3 }}>
          You have {stats.criticalAlerts} trends flagged for review!
        </Alert>
      )}

      {/* Key Metrics Row 1 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(5, 1fr)'
          },
          gap: 3,
          mb: 3
        }}
      >
        <StatCard
          title="Total Patients"
          value={Number(stats.totalPatients || 0).toLocaleString()}
          icon={<People fontSize="large" />}
          color="primary.main"
          subtitle="+12% from last month"
          onClick={() => navigate('/patients')}
        />
        <StatCard
          title="Active Doctors"
          value={stats.totalDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="success.main"
          subtitle="All verified"
          onClick={() => navigate('/doctors?status=active')}
        />
        <StatCard
          title="Active Subscribers"
          value={activeSubscribers}
          icon={<Groups fontSize="large" />}
          color="secondary.main"
          subtitle={`+${newSubscribersThisWeek} this week`}
          onClick={() => navigate('/patient-status-list?category=active_subscribers')}
        />
        <StatCard
          title="Market Executives"
          value={totalMarketExecutives}
          icon={<BusinessCenter fontSize="large" />}
          color="info.main"
          subtitle="Total executives"
          onClick={() => navigate('/market-executives')}
        />
        <StatCard
          title="Trends Flagged"
          value={stats.criticalAlerts}
          icon={<Warning fontSize="large" />}
          color="error.main"
          subtitle="Flagged for review"
          onClick={() => navigate('/critical-patients')}
        />
      </Box>

      {/* Key Metrics Row 2 */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)'
          },
          gap: 3,
          mb: 4
        }}
      >
        <StatCard
          title="Today's Appointments"
          value={stats.todayAppointments}
          icon={<CalendarToday fontSize="large" />}
          color="warning.main"
          onClick={() => navigate('/appointments')}
        />
        <StatCard
          title="Pending Verification"
          value={pendingVerificationDoctors}
          icon={<LocalHospital fontSize="large" />}
          color="warning.main"
          subtitle="Awaiting verification"
          onClick={() => navigate('/doctors?status=inactive')}
        />
        <StatCard
          title="Doctors with No patients"
          value={doctorsWithZeroPatients}
          icon={<LocalHospital fontSize="large" />}
          color="info.main"
          subtitle="No assigned patients"
          onClick={() => navigate('/patient-status-list?category=doctors_no_patients')}
        />
        <Card
          sx={{
            height: '100%',
            cursor: 'pointer',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            }
          }}
          onClick={() => navigate('/patient-status-list?category=male_patients')}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography color="textSecondary" gutterBottom variant="h6">
                  Patients Gender Distribution
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 60 }}>
                      Male:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                      {malePatients}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 60 }}>
                      Female:
                    </Typography>
                    <Typography variant="h5" sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                      {femalePatients}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ color: 'success.main', opacity: 0.8 }}>
                <People fontSize="large" />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
      {/* Charts and Activities */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr'
          },
          gap: 3,
          mb: 4
        }}
      >


        {/* Top 3 Executives */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <Box sx={{ width: '100%' }}>
                <Typography color="textSecondary" gutterBottom variant="h6" sx={{ fontWeight: 'bold' }}>
                  Top 3 Executives
                </Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1.5 }}>
                  By active subscribers
                </Typography>
                {topExecutives.length > 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {topExecutives.map((exec: any, index: number) => (
                      <Box
                        key={exec.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          p: 1,
                          borderRadius: 1,
                          bgcolor: index === 0 ? 'primary.light' : 'action.hover',
                          border: index === 0 ? '1px solid' : 'none',
                          borderColor: index === 0 ? 'primary.main' : 'transparent'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                            {index + 1}
                          </Avatar>
                          <Typography variant="body2" sx={{ fontWeight: index === 0 ? 'bold' : 'medium' }}>
                            {exec.full_name || exec.username || 'Unknown'}
                          </Typography>
                        </Box>
                        <Chip
                          label={`${exec.active_subscribers || 0}`}
                          size="small"
                          color={index === 0 ? 'primary' : 'default'}
                          sx={{ fontWeight: 'bold', minWidth: 45 }}
                        />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    No executives data available
                  </Typography>
                )}
              </Box>
              <Box sx={{ color: 'warning.main', opacity: 0.8, ml: 1 }}>
                <BusinessCenter fontSize="large" />
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Today's Appointments */}
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography 
                variant="h6" 
                onClick={() => navigate('/appointments')}
                sx={{ 
                  cursor: 'pointer',
                  display: 'inline-block',
                  '&:hover': {
                    color: 'primary.main',
                    textDecoration: 'underline'
                  }
                }}
              >
                Today's Appointments
              </Typography>
            </Box>
            
            {todayAppointments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No appointments scheduled for today
                </Typography>
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {todayAppointments.map((appointment: any) => (
                  <ListItem 
                    key={appointment.id} 
                    divider
                    sx={{
                      '&:hover': {
                        bgcolor: 'action.hover',
                      }
                    }}
                  >
                    <ListItemIcon>
                      <AccessTime color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' }
                          }}
                          onClick={() => navigate('/appointments')}
                        >
                          {`${appointment.time instanceof Date && !isNaN(appointment.time.getTime()) ? appointment.time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : appointment.appointment_time} - ${appointment.patientName || appointment.patient_name}`}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="body2" color="textSecondary">
                            <strong>Doctor:</strong> {appointment.doctor_name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip
                                label={appointment.type || appointment.appointment_type}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                label={appointment.status}
                                size="small"
                                color={appointment.status === 'completed' ? 'success' : 'default'}
                                variant="filled"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </Box>
                            <IconButton
                              color="success"
                              size="small"
                              onClick={() => handleMarkAsCompleted(appointment.id)}
                              disabled={appointment.status === 'completed' || updatingAppointment === appointment.id}
                              title={appointment.status === 'completed' ? 'Already completed' : 'Mark as completed'}
                            >
                              {updatingAppointment === appointment.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <CheckCircle />
                              )}
                            </IconButton>
                          </Box>
                        </Box>
                      }
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: '2fr 1fr'
          },
          gap: 3
        }}
      >
        {/* Health Status Trends */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                📊 Weekly Health Status Overview
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel id="trends-filter-label">Vital Type</InputLabel>
                <Select
                  labelId="trends-filter-label"
                  value={trendsFilter}
                  label="Vital Type"
                  onChange={(e: SelectChangeEvent) => setTrendsFilter(e.target.value as string)}
                >
                  <MenuItem value="heartrate">Heart Rate</MenuItem>
                  <MenuItem value="bp">Blood Pressure</MenuItem>
                  <MenuItem value="bmi">BMI Status</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {trendsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                <CircularProgress />
              </Box>
            ) : trendsError ? (
              <Alert severity="error" sx={{ my: 2 }}>
                {trendsError}
              </Alert>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={weeklyTrendsData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="critical" stackId="a" fill="#f44336" name="Needs Priority Review" />
                  <Bar dataKey="warning" stackId="a" fill="#ff9800" name="Needs Review" />
                  <Bar dataKey="normal" stackId="a" fill="#4caf50" name="Within Usual Range" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        {/* New Patients Added */}
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              gutterBottom
              onClick={() => navigate('/patients')}
              sx={{
                cursor: 'pointer',
                display: 'inline-block',
                '&:hover': {
                  color: 'primary.main',
                  textDecoration: 'underline'
                }
              }}
            >
              New Patients Added This Week
            </Typography>
            <Box
              sx={{
                maxHeight: 400,
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': {
                  width: '8px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.3)',
                  },
                },
              }}
            >
              <List>
                {recentActivities
                  .filter((activity) => activity.type === 'patient')
                  .map((activity) => (
                    <ListItem
                      key={activity.id}
                      divider
                      onClick={() => handleActivityClick(activity)}
                      sx={{
                        cursor: (activity.navigateTo || activity.type === 'patient') ? 'pointer' : 'default',
                        transition: 'background-color 0.2s ease',
                        '&:hover': {
                          backgroundColor: (activity.navigateTo || activity.type === 'patient') ? 'action.hover' : 'transparent'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <PersonAdd color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={activity.text}
                        secondary={activity.time}
                      />
                    </ListItem>
                  ))}
                {recentActivities.filter((activity) => activity.type === 'patient').length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No new patients added this week"
                      secondary="Patients added in the last 7 days will appear here"
                    />
                  </ListItem>
                )}
              </List>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}